import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { ShieldAlert } from "lucide-react";

function BrandMark() {
    return (
        <div className="w-9 h-9 bg-[#CCFF00] flex items-center justify-center shrink-0" aria-hidden="true">
            <span className="font-mono font-bold text-[#05070A] text-base leading-none">&gt;_</span>
        </div>
    );
}

export default function LoginPage() {
    const { user, login, loading } = useAuth();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    if (loading) return <div className="p-10 text-cm-muted">Checking session…</div>;
    if (user) return <Navigate to={loc.state?.from || "/"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        const r = await login(email, password);
        setBusy(false);
        if (!r.ok) setErr(r.error);
    };

    return (
        <div className="min-h-screen flex bg-cm-bg" data-testid="login-page">
            {/* Left rail — brand + terminal mockup */}
            <div className="hidden lg:flex w-1/2 relative flex-col justify-between p-12 border-r border-cm-line overflow-hidden">
                <div className="absolute inset-0 nx-grid opacity-70" />
                <div className="absolute inset-0 nx-glow" />

                <div className="relative flex items-center gap-3">
                    <BrandMark />
                    <span className="font-display text-lg tracking-tight">
                        <span className="text-cm-text">Nex</span>
                        <span className="text-cm-brand">Terminal</span>
                    </span>
                </div>

                <div className="relative">
                    <div className="text-xs font-mono uppercase tracking-[0.22em] text-cm-brand">
                        // control plane
                    </div>
                    <h2 className="cm-h1 mt-4 max-w-md leading-tight">
                        Proxmox-native control plane for managed Nextcloud.
                    </h2>
                    <p className="text-cm-muted mt-4 max-w-md text-sm leading-relaxed">
                        REST-first. Multi-tenant. Reseller-grade. Provisioning, updates,
                        autoscaling and audit — fully programmatic.
                    </p>

                    {/* Terminal window mockup */}
                    <div className="mt-8 max-w-md bg-cm-surface/90 border border-cm-line2 overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-cm-bg/60 border-b border-cm-line">
                            <span className="w-3 h-3 rounded-full" style={{ background: "#FF5F56" }} />
                            <span className="w-3 h-3 rounded-full" style={{ background: "#FFBD2E" }} />
                            <span className="w-3 h-3 rounded-full" style={{ background: "#27C93F" }} />
                            <span className="ml-2 font-mono text-xs text-cm-dim">nexterminal — ssh</span>
                        </div>
                        <div className="px-4 py-4 font-mono text-[12px] leading-relaxed">
                            <div><span className="text-cm-brand">$</span> <span className="text-cm-text">nexterminal fleet status</span></div>
                            <div className="text-cm-muted mt-1">clouds       <span className="text-cm-text">332 active</span></div>
                            <div className="text-cm-muted">loadbalancer <span className="text-[#27C93F]">healthy</span></div>
                            <div className="text-cm-muted">tls          <span className="text-[#27C93F]">valid · wildcard</span></div>
                            <div className="mt-1 text-cm-brand">✓ all systems nominal<span className="nx-caret text-cm-brand">▍</span></div>
                        </div>
                    </div>
                </div>

                <div className="relative grid grid-cols-3 gap-px bg-cm-line max-w-md">
                    {["proxmox", "agents", "litellm", "s3", "redis", "loadbalancer"].map((k) => (
                        <div
                            key={k}
                            className="bg-cm-surface py-3 px-3 font-mono text-[11px] uppercase tracking-widest text-cm-dim"
                        >
                            {k}
                        </div>
                    ))}
                </div>
            </div>

            {/* Right — sign-in */}
            <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <BrandMark />
                        <span className="font-display text-lg tracking-tight">
                            <span className="text-cm-text">Nex</span>
                            <span className="text-cm-brand">Terminal</span>
                        </span>
                    </div>

                    <div className="cm-label mb-2">Restricted area</div>
                    <h1 className="cm-h1">Operator sign-in</h1>
                    <p className="text-sm text-cm-muted mt-2">
                        This panel is for NexTerminal superadmins. Reseller and customer access
                        is API-only.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-3">
                        <div>
                            <label className="cm-label">Email</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                className="cm-input mt-1"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoFocus
                                required
                            />
                        </div>
                        <div>
                            <label className="cm-label">Password</label>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                className="cm-input mt-1"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {err && (
                            <div
                                className="cm-card p-3 flex items-start gap-2 border-[#F43F5E]/40 bg-[rgba(244,63,94,0.08)]"
                                data-testid="login-error"
                            >
                                <ShieldAlert size={16} className="text-[#fb6f86] mt-0.5" />
                                <div className="text-xs text-[#fb6f86] flex-1">{err}</div>
                            </div>
                        )}
                        <button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={busy}
                            className="cm-btn-primary w-full disabled:opacity-50"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
